import { useEffect, useState } from "react";
import { apiRequest, downloadExport } from "../api";
import { useAuth } from "../auth";

type Client = {
  id: string;
  phoneNumber?: string | null;
  isActive: boolean;
  notesPrivate?: string | null;
  createdAt: string;
  user: { fullName: string; email: string };
};

export function ClientsPage() {
  const { businessId } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function load(q?: string) {
    if (!businessId) return;
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    const data = await apiRequest<{ clients: Client[] }>(
      `/businesses/${businessId}/bookings/clients/list${qs}`,
    );
    setClients(data.clients);
  }

  useEffect(() => {
    void load().catch((err: Error) => setError(err.message));
  }, [businessId]);

  if (!businessId)
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Clients</p>
          <h1>Your client list</h1>
        </div>
        <div className="cta-row">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() =>
              void downloadExport(businessId, "clients.csv").catch(
                (err: Error) => setError(err.message),
              )
            }
          >
            Export CSV
          </button>
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section>
        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            void load(query).catch((err: Error) => setError(err.message));
          }}
        >
          <label>
            Search clients
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or email"
            />
          </label>
          <button className="btn btn-ghost" type="submit">
            Search
          </button>
        </form>
      </section>

      <section>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>{client.user.fullName}</td>
                <td>{client.user.email}</td>
                <td>{client.phoneNumber ?? "—"}</td>
                <td>{client.isActive ? "Active" : "Inactive"}</td>
                <td>{new Date(client.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {clients.length === 0 ? (
          <p className="notice">No clients yet.</p>
        ) : null}
      </section>
    </>
  );
}
